import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import { Pencil, Trash2, Star } from 'lucide-react';

const AdminNewsContent = () => {
  const { isCollapsed } = useSidebar();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentCategories, setContentCategories] = useState([]);
  const [contentTags, setContentTags] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image: '',
    contentCategory: '',
    contentTags: [],
    author: '',
    status: 'draft',
    featured: false,
  });
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);

  useEffect(() => {
    fetchDropdowns();
  }, []);
  useEffect(() => {
    fetchItems();
  }, [statusFilter, categoryFilter, tagFilter, searchTerm, currentPage, limit]);

  const fetchDropdowns = async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        api.get('/content-categories').catch(() => ({ data: [] })),
        api.get('/content-tags').catch(() => ({ data: [] })),
      ]);
      setContentCategories(Array.isArray(cRes.data) ? cRes.data : []);
      setContentTags(Array.isArray(tRes.data) ? tRes.data : []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit, status: statusFilter };
      if (categoryFilter) params.category = categoryFilter;
      if (tagFilter) params.tag = tagFilter;
      if (searchTerm) params.search = searchTerm;
      const res = await api.get('/news', { params });
      setItems(res.data.news || []);
      setPagination(res.data.pagination || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setFormData({ title: '', content: '', excerpt: '', image: '', contentCategory: '', contentTags: [], author: '', status: 'draft', featured: false });
    setSelectedTagIds([]);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      setFormLoading(true);
      const res = await api.get(`/news/admin/${id}`);
      const b = res.data;
      setFormData({
        title: b.title || '',
        content: b.content || '',
        excerpt: b.excerpt || '',
        image: b.image || '',
        contentCategory: (b.contentCategory && (b.contentCategory._id || b.contentCategory)) || '',
        contentTags: [],
        author: b.author || '',
        status: b.status || 'draft',
        featured: !!b.featured,
      });
      setSelectedTagIds((b.contentTags || []).map((t) => (t._id || t).toString()));
      setEditingId(id);
      setShowModal(true);
    } catch (e) {
      showError(e.response?.data?.message || 'Error loading');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    fetchItems();
  };

  const handleTagToggle = (id) => {
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showError('Category name is required');
      return;
    }
    setCreatingCategory(true);
    try {
      const res = await api.post('/content-categories', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
        active: true,
      });
      await fetchDropdowns();
      setFormData((prev) => ({ ...prev, contentCategory: res.data._id }));
      setShowCreateCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
      showSuccess('Content category created.');
    } catch (e) {
      showError(e.response?.data?.message || 'Error creating category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      showError('Tag name is required');
      return;
    }
    setCreatingTag(true);
    try {
      const res = await api.post('/content-tags', {
        name: newTagName.trim(),
        active: true,
      });
      await fetchDropdowns();
      setSelectedTagIds((prev) => [...prev, res.data._id]);
      setShowCreateTagModal(false);
      setNewTagName('');
      showSuccess('Content tag created.');
    } catch (e) {
      showError(e.response?.data?.message || 'Error creating tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        contentCategory: formData.contentCategory || undefined,
        contentTags: selectedTagIds,
      };
      if (editingId) {
        await api.put(`/news/${editingId}`, payload);
        showSuccess('News updated.');
      } else {
        await api.post('/news', payload);
        showSuccess('News created.');
      }
      closeModal();
    } catch (e) {
      showError(e.response?.data?.message || 'Error saving');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = (id) => {
    showConfirm('Delete this news item?', async () => {
      try {
        await api.delete(`/news/${id}`);
        showSuccess('Deleted.');
        fetchItems();
      } catch (e) {
        showError(e.response?.data?.message || 'Error deleting');
      }
    });
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  return (
    <>
      <SEO title="News – Admin" noindex />
      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-50">News</h1>
            <button type="button" onClick={openAdd} className="btn-primary text-sm">Add News</button>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search..."
              className="flex-1 min-w-[180px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="all">All status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="">All categories</option>
              {contentCategories.map((c) => (
                <option key={c._id} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <select value={tagFilter} onChange={(e) => { setTagFilter(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
              <option value="">All tags</option>
              {contentTags.map((t) => (
                <option key={t._id} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <PageLoader message="Loading..." />
          ) : (
            <>
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((b) => (
                      <tr key={b._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-50">{b.title}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{b.contentCategory?.name || '–'}</td>
                        <td className="px-4 py-3"><span className={`badge text-xs ${b.status === 'published' ? 'badge-success' : 'badge-warning'}`}>{b.status}</span></td>
                        <td className="px-4 py-3">{b.featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : '–'}</td>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => openEdit(b._id)} className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1 mr-3"><Pencil className="w-4 h-4" /> Edit</button>
                          <button type="button" onClick={() => handleDelete(b._id)} className="text-error-600 dark:text-error-400 hover:underline inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(!items || items.length === 0) && <div className="card p-12 text-center text-gray-600 dark:text-gray-400">No news.</div>}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                    onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  />
                </div>
              )}
            </>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8">
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">{editingId ? 'Edit News' : 'Add News'}</h2>
                  <button type="button" onClick={closeModal} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Title *</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="input-field" />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Excerpt</label>
                    <textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={2} className="input-field" />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Image URL</label>
                    <input type="text" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="https://..." className="input-field" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category</label>
                      <div className="flex gap-2">
                        <select value={formData.contentCategory} onChange={(e) => setFormData({ ...formData, contentCategory: e.target.value })} className="input-field flex-1">
                          <option value="">None</option>
                          {contentCategories.map((c) => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowCreateCategoryModal(true)}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
                          title="Create New Content Category"
                        >
                          + New
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Author</label>
                      <input type="text" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="input-field" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold">Tags</label>
                      <button
                        type="button"
                        onClick={() => setShowCreateTagModal(true)}
                        className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
                        title="Create New Content Tag"
                      >
                        + New Tag
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {contentTags.map((t) => (
                        <button
                          key={t._id}
                          type="button"
                          onClick={() => handleTagToggle(t._id)}
                          className={`px-3 py-1.5 rounded-full text-sm ${selectedTagIds.includes((t._id || t).toString()) ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Status</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="input-field">
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 self-end">
                      <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="rounded" />
                      <span className="text-gray-700 dark:text-gray-300">Featured</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Content *</label>
                    <div className="rich-text-editor">
                      <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={(v) => setFormData({ ...formData, content: v })}
                        modules={quillModules}
                        placeholder="Write content..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button type="submit" disabled={formLoading} className="btn-primary flex-1">{(formLoading ? 'Saving...' : (editingId ? 'Update' : 'Create'))}</button>
                    <button type="button" onClick={closeModal} className="btn-secondary flex-1">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Create Content Category Modal */}
          {showCreateCategoryModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">Create Content Category</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter category name"
                      className="input-field"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Description (Optional)</label>
                    <textarea
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      placeholder="Enter description"
                      rows={3}
                      className="input-field"
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={creatingCategory || !newCategoryName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {creatingCategory ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateCategoryModal(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                      disabled={creatingCategory}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Content Tag Modal */}
          {showCreateTagModal && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-50">Create Content Tag</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Enter tag name"
                      className="input-field"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateTag}
                      disabled={creatingTag || !newTagName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50"
                    >
                      {creatingTag ? 'Creating...' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowCreateTagModal(false); setNewTagName(''); }}
                      disabled={creatingTag}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminNews = () => (
  <AdminLayout>
    <AdminNewsContent />
  </AdminLayout>
);

export default AdminNews;
